<template>
  <div class="main-container">
    <div class="blue-banner-management-page">
      <div class="blue-banner-management-page__left">
        <main-menu></main-menu>
      </div>
      <div class="blue-banner-management-page__right">
        <div class="form--overflow">
          <v-card>
            <v-card-title class="d-flex">
              <h3>Messages Management</h3>
              <v-btn class="ml-4" color="success" @click="addNewBanner">Create Message</v-btn>
              <v-btn 
                color="success" 
                @click="saveBannerConfig"
                :loading="saving"
                :disabled="saving || !hasChanges"
                class="ml-2"
              >
                <v-icon left>mdi-content-save</v-icon>
                Save All
              </v-btn>
              <v-btn 
                color="info" 
                @click="loadConfig"
                :loading="loading"
                class="ml-2"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh
              </v-btn>
            </v-card-title>

            <v-card-text>
              <!-- Error/Success Messages -->
              <div v-if="error" class="mb-4">
                <v-alert type="error" dismissible @click:close="error = ''">
                  <div class="d-flex align-center">
                    <div class="flex-grow-1">
                      {{ error }}
                    </div>
                    <v-btn 
                      v-if="error.includes('refresh') || error.includes('Authentication') || error.includes('MSAL')"
                      color="white" 
                      text 
                      small 
                      @click="refreshPage"
                      class="ml-2"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Refresh Page
                    </v-btn>
                  </div>
                </v-alert>
              </div>

              <div v-if="success" class="mb-4">
                <v-alert type="success" dismissible @click:close="success = ''">
                  {{ success }}
                </v-alert>
              </div>

              
              <!-- Column Filters -->
              <v-expansion-panels class="mb-4">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <v-icon left>mdi-filter</v-icon>
                    Column Filters
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model="filters.key"
                          label="Filter by Key"
                          clearable
                          dense
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model="filters.translations"
                          label="Filter by Message"
                          clearable
                          dense
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model="filters.rules"
                          label="Filter by Target Games"
                          clearable
                          dense
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="3">
                        <v-text-field
                          v-model="filters.domains"
                          label="Filter by Domains"
                          clearable
                          dense
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-btn 
                          color="error" 
                          @click="clearFilters" 
                          small
                        >
                          Clear All Filters
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <!-- Results Counter -->
              <div class="mb-4">
                <v-chip color="info" small>
                  Showing {{ getPaginationText() }} messages
                </v-chip>
              </div>

              <!-- Data Table -->
              <v-data-table 
                sort-by="name" 
                :loading="loading" 
                :headers="headers" 
                :items="paginatedItems" 
                hide-default-footer
                class="elevation-1"
              >
                <template v-slot:item.translations="{ item }">
                  <div class="message-preview">
                    {{ item.translations && item.translations.en ? 
                      (item.translations.en.length > 50 ? item.translations.en.substring(0, 50) + '...' : item.translations.en) : 
                      'No message' }}
                  </div>
                </template>
                <template v-slot:item.rules="{ item }">
                  <div class="games-preview">
                    <div v-if="item.rules && item.rules.games && item.rules.games.length > 0">
                      <div class="games-chips">
                        <v-chip 
                          v-for="game in item.rules.games.slice(0, 3)" 
                          :key="game" 
                          x-small 
                          color="primary" 
                          class="game-chip"
                        >
                          {{ game }}
                        </v-chip>
                        <v-chip 
                          v-if="item.rules.games.length > 3"
                          x-small
                          color="grey"
                          class="more-chip"
                        >
                          +{{ item.rules.games.length - 3 }}
                        </v-chip>
                      </div>
                    </div>
                    <div v-else class="all-text">No limits</div>
                  </div>
                </template>
                <template v-slot:item.domains="{ item }">
                  <div class="domains-preview">
                    <div v-if="item.domains && item.domains.length > 0">
                      <div class="domains-chips">
                        <v-chip 
                          v-for="domain in item.domains.slice(0, 3)" 
                          :key="domain"
                          x-small
                          color="success"
                          class="domain-chip"
                        >
                          {{ domain }}
                        </v-chip>
                        <v-chip 
                          v-if="item.domains.length > 3"
                          x-small
                          color="grey"
                          class="more-chip"
                        >
                          +{{ item.domains.length - 3 }}
                        </v-chip>
                      </div>
                    </div>
                    <div v-else class="all-text">No limits</div>
                  </div>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon class="mr-2" @click="previewItem(item)" color="success" title="Preview message">mdi-eye</v-icon>
                  <v-icon class="mr-2" @click="editItem(item)" color="primary" title="Edit message">mdi-pencil</v-icon>
                  <v-icon class="mr-2" @click="duplicateItem(item)" color="info" title="Duplicate message">mdi-content-duplicate</v-icon>
                  <v-icon @click="deleteItem(item)" color="warning" title="Delete message">mdi-delete</v-icon>
                </template>
              </v-data-table>
              
              <!-- Pagination Controls -->
              <div class="d-flex align-center justify-space-between mt-4">
                <div class="d-flex align-center">
                  <span class="text--secondary mr-4">Items per page:</span>
                  <v-select
                    v-model="itemsPerPage"
                    :items="itemsPerPageOptions"
                    dense
                    outlined
                    hide-details
                    style="max-width: 80px;"
                    @change="onItemsPerPageChange"
                  ></v-select>
                </div>
                
                <div class="d-flex align-center">
                  <span class="text--secondary mr-4">
                    {{ getPaginationText() }}
                  </span>
                  
                  <v-pagination
                    v-model="page"
                    :length="getTotalPages()"
                    :total-visible="7"
                    @input="onPageChange"
                  ></v-pagination>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <!-- Banner Editor Dialog -->
          <v-dialog v-model="editDialog" max-width="800px" persistent>
            <v-card>
              <v-card-title>
                <span class="headline">{{ editingItem ? 'Edit' : 'Create' }} Message</span>
              </v-card-title>
              
              <v-card-text>
                <v-container>
                  <v-row v-if="editingItem" align="start" justify="start">
                    <v-col cols="12" md="6">
                      <v-text-field
                        v-model="editingItem.key"
                        label="Message Key"
                        outlined
                        dense
                        class="text-left"
                        :rules="keyRulesComputed"
                        :error-messages="keyFieldError ? [keyFieldError] : []"
                        @input="keyFieldError = ''"
                        :counter="25"
                        maxlength="25"
                        placeholder="Enter message key"
                        required
                      ></v-text-field>
                    </v-col>
                    
                    <!-- Translations -->
                    <v-col cols="12">
                      <h4>Translations</h4>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingItem.translations.en"
                        label="English (en)"
                        outlined
                        rows="3"
                        placeholder="Enter English translation"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingItem.translations.de"
                        label="German (de)"
                        outlined
                        rows="3"
                        placeholder="Enter German translation"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingItem.translations.es"
                        label="Spanish (es)"
                        outlined
                        rows="3"
                        placeholder="Enter Spanish translation"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingItem.translations.fr"
                        label="French (fr)"
                        outlined
                        rows="3"
                        placeholder="Enter French translation"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-textarea
                        v-model="editingItem.translations.it"
                        label="Italian (it)"
                        outlined
                        rows="3"
                        placeholder="Enter Italian translation"
                      ></v-textarea>
                    </v-col>
                    <v-col cols="12">
                      <v-combobox
                        v-model="editingItem.rules.games"
                        label="Target Games"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        placeholder="Enter game slug or alias like in game page URL"
                        hint="Press Enter to add game slug or alias (part of game page URL)"
                        rows="3"
                      ></v-combobox>
                    </v-col>
                    <v-col cols="12">
                      <v-combobox
                        v-model="editingItem.domains"
                        label="Target Domains"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        placeholder="Enter domain names"
                        hint="Press Enter to add domain"
                        rows="3"
                      ></v-combobox>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeEditDialog">
                  Cancel
                </v-btn>
                <v-btn color="blue darken-1" text @click="saveEditDialog" :disabled="!editingItem || !editingItem.key">
                  {{ editingItemIndex !== null ? 'Update' : 'Create' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Message Preview Dialog -->
          <v-dialog v-model="previewDialog" max-width="800px" persistent>
            <v-card>
              <v-card-title>
                <span class="headline">Message Preview</span>
                <v-spacer></v-spacer>
                <v-btn icon @click="closePreviewDialog">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-card-title>
              
              <v-card-text>
                <div v-if="previewingItem" class="preview-dialog-content">
                  <div class="preview-message-info mb-4">
                    <v-chip color="primary" small class="mr-2">{{ previewingItem.key }}</v-chip>
                    <span class="text--secondary">Message Key</span>
                  </div>
                  
                  <div class="preview-container">
                    <div class="preview-tabs">
                      <v-tabs v-model="previewTabIndex" centered>
                        <v-tab>EN</v-tab>
                        <v-tab>DE</v-tab>
                        <v-tab>ES</v-tab>
                        <v-tab>FR</v-tab>
                        <v-tab>IT</v-tab>
                      </v-tabs>
                    </div>
                    
                    <div class="message-preview-content">
                      <div class="banner-demo">
                        <div class="banner-demo__icon">
                          <v-icon small color="white">mdi-information</v-icon>
                        </div>
                        <div class="banner-demo__text" v-if="previewingItem.translations && previewingItem.translations[currentPreviewLang]"
                             v-html="previewingItem.translations[currentPreviewLang]"></div>
                        <div class="banner-demo__text message-placeholder" v-else>
                          No {{ currentPreviewLang.toUpperCase() }} translation available
                        </div>
                        <div class="banner-demo__close">
                          <v-icon small color="white">mdi-close</v-icon>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Games and Arenas Info -->
                  <div class="preview-meta-info mt-4">
                    <v-row>
                      <v-col cols="6">
                        <div class="meta-section">
                          <h5 class="meta-title">Target Games:</h5>
                          <div v-if="previewingItem.rules && previewingItem.rules.games && previewingItem.rules.games.length > 0">
                            <v-chip 
                              v-for="game in previewingItem.rules.games" 
                              :key="game" 
                              x-small 
                              color="primary" 
                              class="mr-1 mb-1"
                            >
                              {{ game }}
                            </v-chip>
                          </div>
                          <div v-else class="meta-empty">No limits</div>
                        </div>
                      </v-col>
                      <v-col cols="6">
                        <div class="meta-section">
                          <h5 class="meta-title">Domains:</h5>
                          <div v-if="previewingItem.domains && previewingItem.domains.length > 0">
                            <v-chip 
                              v-for="domain in previewingItem.domains" 
                              :key="domain"
                              x-small
                              color="success"
                              class="mr-1 mb-1"
                            >
                              {{ domain }}
                            </v-chip>
                          </div>
                          <div v-else class="meta-empty">No limits</div>
                        </div>
                      </v-col>
                    </v-row>
                  </div>
                </div>
              </v-card-text>
              
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="editItem(previewingItem)" v-if="previewingItem">
                  <v-icon left>mdi-pencil</v-icon>
                  Edit Message
                </v-btn>
                <v-btn color="blue darken-1" text @click="closePreviewDialog">
                  Close
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainMenu from '../MainMenu/MainMenu.vue'
import { BlueBannerService } from '../../services/BlueBannerService.js'
import { ClearCacheService } from '../../services/ClearCacheService.js'
import persistentDialogEsc from '@/mixins/persistentDialogEsc'

export default {
  name: 'BlueBannerManagement',
  components: { MainMenu },
  mixins: [
    persistentDialogEsc({
      editDialog: 'closeEditDialog',
      previewDialog: 'closePreviewDialog',
    }),
  ],
  data() {
    return {
      // Messages array
      messages: [],
      originalMessages: [], // Store original state for comparison
      initialMessages: null, // Preserve initialMessages from backend (nullable)
      
      // Table configuration
      search: '',
      headers: [
        { text: 'Key', value: 'key', sortable: true },
        { text: 'Message', value: 'translations', sortable: false },
        { text: 'Target Games', value: 'rules', sortable: false },
        { text: 'Domains', value: 'domains', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center' },
      ],
      filters: {
        key: '',
        translations: '',
        rules: '',
        domains: ''
      },
      booleanOptions: ['', 'true', 'false'],
      
      // Pagination
      page: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [5, 10, 25, 50, 100],
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['name'],
        sortDesc: [false]
      },
      
      // Dialog state
      editDialog: false,
      editingItem: null,
      editingItemIndex: null,
      previewDialog: false,
      previewingItem: null,
      languages: ['en','de','es','fr','it'],
      previewTabIndex: 0, // index-based tabs
      keyRules: [
        v => !!v || 'Message key is required',
        v => (v && v.length <= 25) || 'Max 25 characters',
        v => /^[A-Za-z0-9]+$/.test(v) || 'Only English letters and digits allowed'
      ],
      keyFieldError: '',
      
      
      // UI state
      saving: false,
      loading: false,
      error: '',
      success: '',
      
      // Service
      bannerService: null
    }
  },
  computed: {
    filteredItems() {
      if (!this.messages || !Array.isArray(this.messages)) {
        return [];
      }
      return this.messages.filter(item => {
        return (
          (!this.filters.key || item.key.toLowerCase().includes(this.filters.key.toLowerCase())) &&
          (!this.filters.translations || (item.translations && Object.values(item.translations).some(translation => 
            translation.toLowerCase().includes(this.filters.translations.toLowerCase())
          ))) &&
          (!this.filters.rules || (item.rules && JSON.stringify(item.rules).toLowerCase().includes(this.filters.rules.toLowerCase()))) &&
          (!this.filters.domains || (item.domains && item.domains.some(domain => domain.toLowerCase().includes(this.filters.domains.toLowerCase()))))
        );
      });
    },
    totalItems() {
      return this.filteredItems.length;
    },
    
    hasChanges() {
      return JSON.stringify(this.messages) !== JSON.stringify(this.originalMessages);
    },
    keyRulesComputed() {
      const base = this.keyRules || []
      const uniqueRule = (v) => {
        if (!v) return true
        const duplicate = this.messages.some((m, idx) => m.key === v && idx !== this.editingItemIndex)
        return duplicate ? 'Message key must be unique' : true
      }
      return [...base, uniqueRule]
    },
    paginatedItems() {
      const start = (this.page - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredItems.slice(start, end)
    },
    currentPreviewLang() {
      const idx = this.previewTabIndex || 0
      return this.languages[idx] || 'en'
    }
  },
  mounted() {
    try {
      // Check if MSAL is available
      if (!this.msal && !this.$msal) {
        throw new Error('MSAL is not available. Please refresh the page and log in again.')
      }
      
      // Initialize service with MSAL context (tokens + methods)
      this.bannerService = new BlueBannerService({ msal: this.msal, $msal: this.$msal })
      
      // Load configuration on mount
      this.loadConfig()
    } catch (error) {
      console.error('Error initializing service:', error)
      this.bannerService = null
      this.error = error.message || 'Failed to initialize service. Please refresh the page and log in again.'
    }
    
  },
  methods: {
    async loadConfig() {
      if (!this.bannerService) {
        this.error = 'Service not initialized. Please refresh the page and log in again.'
        return
      }
      
      // Check if MSAL is still available
      if (!this.msal) {
        this.error = 'Authentication expired. Please refresh the page and log in again.'
        return
      }
      
      this.loading = true
      this.error = ''
      this.success = ''
      
      try {
        const data = await this.bannerService.getBannerConfig()
        
        // Preserve initialMessages if present
        if (data && Array.isArray(data.initialMessages)) {
          this.initialMessages = JSON.parse(JSON.stringify(data.initialMessages))
        } else {
          this.initialMessages = null
        }
        
        // Handle messages array from response
        if (data && data.messages && Array.isArray(data.messages)) {
          this.messages = data.messages.map((item, index) => ({
            ...item,
            // Ensure rules and rules.games are normalized
            rules: {
              ...(item.rules || {}),
              games: Array.isArray(item.rules && item.rules.games) ? item.rules.games : []
            },
            // Map legacy 'arenas' to new 'domains' for UI
            domains: Array.isArray(item.domains) ? item.domains : (Array.isArray(item.arenas) ? item.arenas : [])
          }))
        } else if (Array.isArray(data)) {
          // If data is directly an array of messages
          this.messages = data.map((item, index) => ({
            ...item,
            rules: {
              ...(item.rules || {}),
              games: Array.isArray(item.rules && item.rules.games) ? item.rules.games : []
            },
            domains: Array.isArray(item.domains) ? item.domains : (Array.isArray(item.arenas) ? item.arenas : [])
          }))
        } else {
          this.messages = []
        }
        
        // Store original state for comparison
        this.originalMessages = JSON.parse(JSON.stringify(this.messages))
      } catch (err) {
        console.error('Error loading config:', err)
        this.error = `Failed to load configuration: ${err.message}`
        this.messages = []
      } finally {
        this.loading = false
      }
    },


    async saveBannerConfig() {
      if (!this.bannerService) {
        this.error = 'Service not initialized'
        return
      }

      this.saving = true
      this.error = ''
      this.success = ''

      try {
        // Map UI 'domains' back to backend 'arenas'
        const messagesToSend = this.messages.map(msg => {
          const { domains, arenas, ...rest } = msg
          return {
            ...rest,
            // Ensure rules.games is always an array
            rules: {
              ...(rest.rules || {}),
              games: Array.isArray(rest.rules && rest.rules.games) ? rest.rules.games : []
            },
            arenas: Array.isArray(domains) ? domains : (Array.isArray(arenas) ? arenas : [])
          }
        })
        
        const payload = {
          messages: messagesToSend
        }
        if (this.initialMessages !== null) {
          payload.initialMessages = this.initialMessages
        }
        
        await this.bannerService.saveBannerConfig(payload)
        // Update original state after successful save
        this.originalMessages = JSON.parse(JSON.stringify(this.messages))
        this.success = 'All messages saved successfully!'
        
        // Clear CDN cache for banner messages file
        try {
          await ClearCacheService.clearBannerMessages()
          this.success = 'All messages saved and cache cleared successfully!'
        } catch (e) {
          console.error('Cache clear failed:', e)
          this.success = 'All messages saved successfully! (Cache clear failed)'
        }
      } catch (err) {
        this.error = `Failed to save messages: ${err.message}`
        console.error('Error saving config:', err)
      } finally {
        this.saving = false
      }
    },

    exportConfig() {
      if (!this.messages || this.messages.length === 0) return
      
      const dataStr = JSON.stringify({ messages: this.messages }, null, 2)
      const dataBlob = new Blob([dataStr], { type: 'application/json' })
      
      const link = document.createElement('a')
      link.href = URL.createObjectURL(dataBlob)
      link.download = 'blue-banner-configs.json'
      link.click()
      URL.revokeObjectURL(link.href)
      
      this.success = 'Configurations exported successfully!'
    },

    importConfig() {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = '.json'
      
      input.onchange = (event) => {
        const file = event.target.files[0]
        if (!file) return
        
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const content = e.target.result
            this.jsonText = content
            this.validateJson()
            
            if (this.isValidJson) {
              this.loadFromJson()
            }
          } catch (err) {
            this.error = `Failed to import file: ${err.message}`
          }
        }
        reader.readAsText(file)
      }
      
      input.click()
    },

    clearFilters() {
      this.filters = {
        key: '',
        translations: '',
        rules: '',
        domains: ''
      }
    },

    addNewBanner() {
      this.editingItem = {
        key: '',
        translations: {
          en: '',
          de: '',
          es: '',
          fr: '',
          it: ''
        },
        rules: {
          games: []
        },
        domains: []
      }
      this.editingItemIndex = null
      this.editDialog = true
    },

    editItem(item) {
      const index = this.messages.findIndex(message => message.key === item.key)
      this.editingItem = { ...item }
      this.editingItemIndex = index
      this.editDialog = true
    },

    previewItem(item) {
      console.log('=== PREVIEW ITEM DEBUG ===')
      console.log('Original item:', item)
      console.log('Item translations:', item.translations)
      console.log('EN text:', item.translations && item.translations.en)
      
      // Deep copy the item to avoid reference issues
      this.previewingItem = JSON.parse(JSON.stringify(item))
      this.previewTabIndex = 0 // Reset to English tab
      
      console.log('After copy - previewingItem:', this.previewingItem)
      console.log('After copy - translations:', this.previewingItem.translations)
      console.log('After copy - EN text:', this.previewingItem.translations && this.previewingItem.translations.en)
      
      // Use nextTick to ensure the dialog opens after data is set
      this.$nextTick(() => {
        this.previewDialog = true
        console.log('Dialog opened, previewingItem:', this.previewingItem)
      })
    },

    duplicateItem(item) {
      const index = this.messages.findIndex(message => message.key === item.key)
      const duplicatedMessage = {
        ...item,
        key: `${item.key}_copy`
      }
      
      this.messages.splice(index + 1, 0, duplicatedMessage)
      this.success = 'Message duplicated successfully!'
    },

    async deleteItem(item) {
      const { isConfirmed } = await this.$swal.fire({
        icon: 'warning',
        title: 'Are you sure you want to delete this message?',
        showCancelButton: true
      });
      
      if (isConfirmed) {
        const index = this.messages.findIndex(message => message.key === item.key)
        if (index !== -1) {
          this.messages.splice(index, 1)
          this.success = 'Message deleted successfully!'
        }
      }
    },

    closeEditDialog() {
      this.editDialog = false
      this.editingItem = null
      this.editingItemIndex = null
    },

    closePreviewDialog() {
      this.previewDialog = false
    },

    saveEditDialog() {
      if (!this.editingItem || !this.editingItem.key) {
        this.keyFieldError = 'Message key is required'
        return
      }

      if (!/^[A-Za-z0-9]+$/.test(this.editingItem.key)) {
        this.keyFieldError = 'Message key must contain only English letters and digits'
        return
      }

      if (this.editingItem.key.length > 25) {
        this.keyFieldError = 'Message key must be 25 characters or fewer'
        return
      }

      // Key uniqueness check (exclude current editing index)
      const duplicate = this.messages.some((m, idx) => m.key === this.editingItem.key && idx !== this.editingItemIndex)
      if (duplicate) {
        this.keyFieldError = 'Message key must be unique'
        return
      }
 
      const isUpdate = this.editingItemIndex !== null
      
      if (isUpdate) {
        // Update existing item
        this.messages[this.editingItemIndex] = { ...this.editingItem }
      } else {
        // Add new item
        this.messages.push({ ...this.editingItem })
      }
      
      this.closeEditDialog()
      this.success = isUpdate ? 'Message updated successfully! Do not forget "Save All" If you finish changes.' : 'Message created successfully!'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString()
    },

    // Pagination methods
    onPageChange(page) {
      this.page = page
      this.options.page = page
    },

    onItemsPerPageChange() {
      this.page = 1
      this.options.page = 1
      this.options.itemsPerPage = this.itemsPerPage
    },

    getTotalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    },

    getPaginationText() {
      if (!this.totalItems || this.totalItems === 0) {
        return '0 of 0'
      }
      const start = (this.page - 1) * this.itemsPerPage + 1
      const end = Math.min(this.page * this.itemsPerPage, this.totalItems)
      return `${start}-${end} of ${this.totalItems}`
    },

    refreshPage() {
      window.location.reload()
    }
  }
}
</script>

<style scoped lang="scss" src="./BlueBannerManagement.scss"></style>
